import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import api from '@/lib/api';

/* Gastpass (11.10.2026): öffentliche Kaufseite für Nichtmitglieder — Drop-In oder P1 Kickstart
   für Strength & Conditioning (Samstag) und HIIT (Sonntag). Bezahlt wird bei Stripe; gebucht wird danach
   in der P1 App, weil dort die Plätze verwaltet werden. Die Seite zeigt nie Daten aus dem CRM. */
const euro = (v) => `${Number(v || 0).toFixed(2).replace('.', ',')} €`;
const datum = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('.') : '');

export default function GastpassKaufPage() {
  const [produkte, setProdukte] = useState([]);
  const [info, setInfo] = useState({ gastplaetze: 4, absagefrist_stunden: 2 });
  const [produkt, setProdukt] = useState('einzel');
  const [vorname, setVorname] = useState('');
  const [nachname, setNachname] = useState('');
  const [email, setEmail] = useState('');
  const [telefon, setTelefon] = useState('');
  const [ok, setOk] = useState(false);
  const [sofort, setSofort] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [bezahlt, setBezahlt] = useState(null);       // { status, name, einheiten, gueltig_bis }
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('bezahlt');
  const abgebrochen = params.get('abgebrochen');

  useEffect(() => {
    // alle 30 s neu: der Zähler der Kickstart-Plätze soll sinken, sobald jemand bucht
    const laden = () => api.get('/gastpass/oeffentlich/produkte').then((r) => { setProdukte(r.data.produkte || []); setInfo(r.data); }).catch(() => {});
    laden();
    const t = setInterval(laden, 30000);
    return () => clearInterval(t);
  }, []);
  const ks = info.kickstart;
  const ausgebucht = !!ks && ks.frei <= 0;
  useEffect(() => { if (ausgebucht && produkt === 'paket') setProdukt('einzel'); }, [ausgebucht, produkt]);
  useEffect(() => {
    if (!sessionId) return undefined;
    let n = 0; let t;
    const pruefen = () => api.get('/gastpass/oeffentlich/bestaetigen', { params: { session_id: sessionId } })
      .then((r) => { setBezahlt(r.data); if (r.data.status === 'offen' && n++ < 8) t = setTimeout(pruefen, 2500); })
      .catch(() => setBezahlt({ status: 'unbekannt' }));
    pruefen();
    return () => clearTimeout(t);
  }, [sessionId]);

  const kaufen = async (e) => {
    e.preventDefault();
    setFehler('');
    if (!ok || !sofort) { setFehler('Bitte beide Bestätigungen anhaken.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/gastpass/oeffentlich/kauf', { vorname: vorname.trim(), nachname: nachname.trim(), email: email.trim(), telefon: telefon.trim(), produkt, bedingungen: ok, sofortbeginn: sofort });
      window.location.href = r.data.url;
    } catch (err) {
      const d = err?.response?.data?.detail;
      setFehler(typeof d === 'string' ? d : 'Das hat nicht geklappt. Bitte noch einmal versuchen.');
      setBusy(false);
    }
  };

  const rahmen = (kinder) => (
    <div className="min-h-screen bg-[#0d0d0d] text-white px-4 py-10">
      <div className="max-w-lg mx-auto space-y-6">
        <div>
          <img src="/logo.png" alt="P1 Performance Center" className="h-20 mx-auto mb-4 object-contain" />
          <h1 className="text-3xl font-extrabold mt-1">Strength &amp; Conditioning als Gast</h1>
          <p className="text-white/70 mt-2 text-sm">Samstag Strength &amp; Conditioning, Sonntag HIIT. Ohne Mitgliedschaft, mit festem Platz über die P1 App.</p>
        </div>
        {kinder}
        <p className="text-[11px] text-white/40">P1 Performance Center GmbH · Georg-Stieß-Straße 2 · 75181 Pforzheim · <a className="underline" href="/impressum">Impressum</a> · <a className="underline" href="/datenschutz">Datenschutz</a> · <a className="underline" href="/agb">AGB</a></p>
      </div>
    </div>
  );

  if (sessionId) {
    return rahmen(
      <div className="rounded-2xl bg-white/5 border border-white/10 p-6 space-y-3" data-testid="gastpass-bezahlt">
        {!bezahlt || bezahlt.status === 'offen' ? (
          <p className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Zahlung wird bestätigt …</p>
        ) : bezahlt.status === 'bezahlt' ? (
          <>
            <p className="flex items-center gap-2 text-lg font-bold"><span className="w-7 h-7 rounded-full bg-[#ef7c23] text-black flex items-center justify-center"><Check className="w-4 h-4" /></span> Bezahlt — danke!</p>
            <p className="text-sm text-white/80">{bezahlt.name}: {bezahlt.einheiten} {bezahlt.einheiten === 1 ? 'Einheit' : 'Einheiten'}, gültig bis {datum(bezahlt.gueltig_bis)}.</p>
            <p className="text-sm text-white/80">So geht es weiter: P1 App öffnen, im Kursplan Strength &amp; Conditioning (Samstag) oder HIIT (Sonntag) wählen und den Platz buchen. Die Einheit wird dabei automatisch abgezogen.</p>
          </>
        ) : (
          <p className="text-sm text-white/80">Die Zahlung konnte nicht bestätigt werden. Wurde Geld abgebucht, melde dich bitte unter info@p1-pforzheim.de — wir schalten dich frei.</p>
        )}
      </div>,
    );
  }

  return rahmen(
    <form onSubmit={kaufen} className="space-y-4" data-testid="gastpass-form">
      {abgebrochen && <p className="rounded-xl bg-amber-500/15 border border-amber-400/40 text-amber-100 text-sm px-3 py-2">Die Zahlung wurde abgebrochen. Es wurde nichts abgebucht.</p>}
      {ks && (
        <div className="rounded-2xl border border-[#ef7c23]/50 bg-[#ef7c23]/10 p-4" data-testid="kickstart-zaehler">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm font-semibold">P1 Kickstart</span>
            <span className="text-sm">{ausgebucht ? 'Aktuell ausgebucht' : <><span className="text-2xl font-extrabold text-[#f68f3f] tabular-nums">{ks.frei}</span> von {ks.plaetze} Plätzen frei</>}</span>
          </div>
          <div className="flex gap-1.5 mt-3" aria-hidden="true">
            {Array.from({ length: ks.plaetze }).map((_, i) => (
              <span key={i} className={`h-2 flex-1 rounded-full ${i < ks.frei ? 'bg-[#ef7c23]' : 'bg-white/15'}`} />
            ))}
          </div>
          {ausgebucht && <p className="text-xs text-white/70 mt-2">Sobald ein Platz frei wird, ist der Kickstart wieder buchbar. Das Drop-In geht jederzeit.</p>}
        </div>
      )}
      <div className="grid gap-3">
        {produkte.map((p) => { const zu = p.id === 'paket' && ausgebucht; return (
          <label key={p.id} className={`rounded-2xl border p-4 flex items-center justify-between gap-3 ${zu ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${produkt === p.id ? 'border-[#ef7c23] bg-[#ef7c23]/10' : 'border-white/15 bg-white/5'}`}>
            <span className="flex items-center gap-3">
              <input type="radio" name="produkt" className="accent-[#ef7c23]" disabled={zu} checked={produkt === p.id} onChange={() => setProdukt(p.id)} data-testid={`gastpass-${p.id}`} />
              <span><span className="block font-semibold">{p.titel || p.name}</span><span className="block text-xs text-white/60">{p.kurz}</span></span>
            </span>
            <span className="text-xl font-extrabold text-[#f68f3f]">{euro(p.preis)}</span>
          </label>
        ); })}
      </div>
      <div className="rounded-2xl bg-white/5 border border-white/10 p-4 space-y-3">
        <p className="text-sm text-white/80">Gebucht wird in der P1 App. Hast du dort schon ein Konto, gib dieselbe E-Mail und Handynummer an. Bist du neu, legen wir dich mit diesen Angaben an — danach meldest du dich in der App mit Name und Handynummer an.</p>
        <div className="grid grid-cols-2 gap-2">
          <input required value={vorname} onChange={(e) => setVorname(e.target.value)} placeholder="Vorname" autoComplete="given-name"
            className="w-full h-11 rounded-xl bg-black/40 border border-white/15 px-3 text-sm" data-testid="gastpass-vorname" />
          <input required value={nachname} onChange={(e) => setNachname(e.target.value)} placeholder="Nachname" autoComplete="family-name"
            className="w-full h-11 rounded-xl bg-black/40 border border-white/15 px-3 text-sm" data-testid="gastpass-nachname" />
        </div>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-Mail" autoComplete="email"
          className="w-full h-11 rounded-xl bg-black/40 border border-white/15 px-3 text-sm" data-testid="gastpass-email" />
        <input type="tel" required value={telefon} onChange={(e) => setTelefon(e.target.value)} placeholder="Handynummer" autoComplete="tel"
          className="w-full h-11 rounded-xl bg-black/40 border border-white/15 px-3 text-sm" data-testid="gastpass-telefon" />
      </div>
      <ul className="text-xs text-white/60 space-y-1 list-disc pl-4">
        <li>Je Kurs gibt es höchstens {info.gastplaetze} Gastplätze. Der Platz ist erst mit der Buchung in der App fest.</li>
        <li>Absagen bis {info.absagefrist_stunden} Stunden vor Kursbeginn: die Einheit bleibt erhalten. Danach oder bei Nichterscheinen gilt sie als genutzt; eine Erstattung gibt es nur bei triftigem Grund.</li>
        {info.einstieg_hinweis && <li><strong className="text-white">{info.einstieg_hinweis}</strong></li>}
        <li>Das Drop-In gilt 30 Tage, P1 Kickstart drei Monate ab Zahlung. P1 Kickstart ist ein Einstiegsangebot: nur für Neukunden und nur einmal je Person.</li>
      </ul>
      <label className="flex items-start gap-2 text-xs text-white/80 cursor-pointer">
        <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="accent-[#ef7c23] mt-0.5" data-testid="gastpass-ok" />
        <span>Ich akzeptiere die <a className="underline" href="/agb#gastpass" target="_blank" rel="noreferrer">Bedingungen für den Gastpass</a> und habe die <a className="underline" href="/agb#widerruf" target="_blank" rel="noreferrer">Widerrufsbelehrung</a> sowie die <a className="underline" href="/datenschutz" target="_blank" rel="noreferrer">Datenschutzerklärung</a> zur Kenntnis genommen.</span>
      </label>
      <label className="flex items-start gap-2 text-xs text-white/80 cursor-pointer">
        <input type="checkbox" checked={sofort} onChange={(e) => setSofort(e.target.checked)} className="accent-[#ef7c23] mt-0.5" data-testid="gastpass-sofort" />
        <span>Ich verlange ausdrücklich, dass das P1 Performance Center schon vor Ablauf der Widerrufsfrist mit der Leistung beginnt. Mir ist bekannt, dass ich bei einem Widerruf die bis dahin genutzten Einheiten anteilig bezahlen muss.</span>
      </label>
      <p className="text-sm text-white/80" data-testid="gastpass-summe">Du bestellst: <b>{produkt === 'paket' ? 'P1 Kickstart mit 12 Einheiten' : 'ein Drop-In (eine Einheit)'}</b> für <b>{euro((produkte.find((p) => p.id === produkt) || {}).preis)}</b> inkl. USt. Einmalzahlung, kein Abo.</p>
      {fehler && <p className="rounded-xl bg-red-500/15 border border-red-400/40 text-red-100 text-sm px-3 py-2" data-testid="gastpass-fehler">{fehler}</p>}
      <button type="submit" disabled={busy} className="w-full h-12 rounded-xl bg-[#ef7c23] text-black font-extrabold tracking-wide disabled:opacity-50" data-testid="gastpass-kaufen">
        {busy ? 'Weiter zu Stripe …' : 'Zahlungspflichtig bestellen'}
      </button>
    </form>,
  );
}
